import { remove } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    componentAutoReaction,
    Enums,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    Nullable,
    SelectInput,
    TasksStore,
    toArray,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { Button, Form, FormGroup } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { ActionRecipientItem } from './ActionRecipientItem';
import { AddRecipientItem } from './AddRecipientItem';
import { AlertActionsStore } from './AlertActionsStore';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';

interface IOption {
    label: string;
    value: string;
}

export interface ICreateOrEditAlertActionModalProps {
    onClosed(alertAction: Mri.DisplayAlerts.AlertAction): void;
    readonly modalHandle: ModalHandle;
    readonly viewState: 'create' | 'edit';
    readonly alertActionGuid?: string;
}

@autoinject
@observer
export class CreateOrEditDisplayAlertActionModal extends React.Component<ICreateOrEditAlertActionModalProps> {
    public render() {
        return (
            <Modal
                title={this.modalTitle}
                modalHandle={this.props.modalHandle}
                footer={this.footer}
                size="lg"
                className="my-2"
                onOpened={this.onOpenedAsync}
                onClosed={this.onModalClosed}
            >
                <div>
                    {this.alertActionForm && (
                        <Form onSubmit={this.submitAsync}>
                            <FormGroup color={this.alertActionForm.fields.name.state}>
                                <label>
                                    <strong>Name</strong>
                                </label>

                                <Input
                                    type="text"
                                    field={this.alertActionForm.fields.name}
                                    placeholder="Name"
                                    required
                                />
                            </FormGroup>

                            <FormGroup color={this.alertActionForm.fields.comment.state}>
                                <label>
                                    <strong>Comments</strong>
                                </label>

                                <Input
                                    type="text"
                                    field={this.alertActionForm.fields.comment}
                                    placeholder="Comments..."
                                />
                            </FormGroup>

                            <FormGroup color={this.alertActionForm.fields.account.state}>
                                <label>
                                    <strong>Account</strong>{' '}
                                    <small>*Optional* Only use if making an alert for a specific customer</small>
                                </label>

                                <SelectInput
                                    options={this.accountOptions}
                                    field={this.alertActionForm.fields.account}
                                    placeholder="Accounts"
                                    required={!this.accountStore.isInRole('MriAdmin')}
                                />
                            </FormGroup>

                            <FormGroup color={this.alertActionForm.fields.actionRecipients.state}>
                                <label>
                                    <strong>Recipients</strong>
                                </label>

                                <AddRecipientItem
                                    alertActionForm={this.alertActionForm}
                                    internal={this.alertActionForm.fields.account.value == null}
                                    onChange={this.addRecipient}
                                />

                                <table className="table border-top-0 mt-1">
                                    <thead>
                                        <tr>
                                            <th scope="col">Email</th>
                                            <th scope="col">Phone Number</th>
                                            <th scope="col">Type</th>
                                            <th scope="col">Delete</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.alertActionForm.fields.actionRecipients.value.map((r, k) => (
                                            <tr key={k}>
                                                <td scope="row">
                                                    {this.alertActionsStore.recipients.get(r.value)?.email ||
                                                        r.value ||
                                                        '-'}
                                                </td>
                                                <td scope="row">
                                                    {this.alertActionsStore.recipients.get(r.value)?.phoneNumber || '-'}
                                                </td>
                                                <td>{Enums.toString(Enums.RecipientType, r.type)}</td>
                                                <td>
                                                    <a
                                                        className="btn btn-link text-danger"
                                                        color="danger"
                                                        onClick={() => this.removeRecipient(r)}
                                                    >
                                                        <BsTrash className="" />
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </FormGroup>
                        </Form>
                    )}
                </div>
            </Modal>
        );
    }

    //Private Functions
    @bind
    @action
    private async submitAsync(event: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) {
        if (!this.alertActionForm) throw new Error('Form does not exist');

        event.preventDefault();
        if (!this.alertActionForm.fields.comment.value) {
            this.alertActionForm.fields.comment.setValue('');
        }
        this.alertActionForm.onSubmit();
        if (this.alertActionForm.validate()) {
            this.loading = true;
            const viewState = this.props.viewState.charAt(0).toUpperCase + this.props.viewState.slice(1);
            const task = this.tasksStore.startSessionTask(
                `${this.props.viewState == 'create' ? 'Create' : 'Edit'} Action ${this.alertActionForm.values.name}`
            );

            try {
                if (this.props.viewState == 'edit') {
                    await this.alertActionsStore.updateAsync(
                        this.alertActionForm.values.alertActionGuid,
                        this.alertActionForm.values
                    );
                } else if (this.props.viewState == 'create') {
                    await this.alertActionsStore.createAsync(this.alertActionForm.values);
                }

                task.complete();
                this.props.modalHandle.close();
                this.alertActionForm.clear();
            } catch (e: any) {
                if (e.modelState) {
                    this.alertActionForm.clearGlobalErrors();
                    this.alertActionForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.error('The Form is invalid');
        }
    }

    @bind
    @action
    private async onOpenedAsync() {
        try {
            await this.accountsStore.loadAllAsync();
        } catch (e: any) {
            this.tasksStore.toastErrorTask('Error', e);
        }

        let account: string | null;

        if (!this.accountStore.isInRole('MriAdmin') && this.accountStore.user?.accounts.length == 1) {
            account = this.accountStore.user.accounts[0];
        } else {
            account = null;
        }

        if (this.alertAction && this.props.viewState == 'edit') {
            this.alertActionForm = new FormStore<Mri.DisplayAlerts.AlertAction>(this.alertAction);
        } else {
            const model: Nullable<Mri.DisplayAlerts.AlertAction> = {
                alertActionGuid: uuidv4generate(),
                name: '',
                comment: '',
                internal: false,
                portal: false,
                actionRecipients: [],
                account,
            } as any;

            this.alertActionForm = new FormStore<Mri.DisplayAlerts.AlertAction>(model);
        }

        componentAutoReaction(this, () => this.alertActionForm!.fields.account.value, this.loadRecipientsAsync);
    }

    @bind
    @action
    private async loadRecipientsAsync() {
        try {
            // Clear recipients before loading new ones
            this.alertActionsStore.recipients.clear();
            this.alertActionForm?.fields.actionRecipients.clear();

            await this.alertActionsStore.loadRecipientsAsync({
                params: {
                    accountFilter: this.alertActionForm?.values.account,
                },
            });
        } catch (e: any) {
            this.tasksStore.toastErrorTask('Error', e);
        }
    }

    @bind
    @action
    private onModalClosed() {
        if (this.alertActionForm) {
            this.props.onClosed(this.alertActionForm.values);
        }
    }

    @bind
    @action
    private addRecipient(recipient: Mri.DisplayAlerts.ActionRecipient) {
        const field = this.alertActionForm?.fields.actionRecipients;
        if (!field) throw new Error('Undefined');

        const value = field.value;
        field.onChange([...value, recipient]);
    }

    @bind
    @action
    private removeRecipient(recipient: Mri.DisplayAlerts.ActionRecipient) {
        const field = this.alertActionForm?.fields.actionRecipients;
        if (!field) throw new Error('Undefined');

        const value = field.value;
        field.onChange(value.filter(o => !(o.type == recipient.type && o.value == recipient.value)));
    }

    @computed
    private get accountOptions() {
        return toArray(this.accountsStore.accounts).map(a => {
            return {
                value: a.accountNumber,
                label: a.accountName,
            };
        });
    }

    @computed
    private get alertAction() {
        if (this.props.alertActionGuid) return this.alertActionsStore.alertActions.get(this.props.alertActionGuid);
    }

    @computed
    private get footer() {
        let statusTitle: string = '';
        switch (this.props.viewState) {
            default:
            case 'edit':
                statusTitle = 'Update Action';
                break;
            case 'create':
                statusTitle = 'Create Action';
                break;
        }

        return (
            <Button color="primary" onClick={this.submitAsync} disabled={this.loading}>
                <Loader
                    loading={this.loading}
                    showSpinner
                    message={this.props.viewState == 'create' ? 'Creating...' : 'Updating...'}
                />
                {!this.loading && statusTitle}
            </Button>
        );
    }

    @computed
    private get modalTitle() {
        switch (this.props.viewState) {
            default:
            case 'edit':
                return 'Edit Alert Action';
            case 'create':
                return 'Create Alert Action';
        }
    }

    @observable
    private alertActionForm?: FormStore<Mri.DisplayAlerts.AlertAction>;
    @observable
    private loading = false;
    @inject
    private accountStore!: AccountStore;
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
}
